import { LabelTagRecord, SpecialTagTypeEnum, TagSubscriptionPreference } from "libs/schema";
import { toast } from "~/environment/toast-service";
import { withActionContext, write } from "./write";
import { canArchiveLabel } from "~/utils/tag-utils";
import { oneLine } from "common-tags";
import * as actions from "libs/actions";
import { InvalidActionStateError } from "libs/actions";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { AlertDialogState } from "~/dialogs/alert/AlertDialog";

/* -------------------------------------------------------------------------------------------------
 * createGroup
 * -------------------------------------------------------------------------------------------------
 */

export const createGroup = withActionContext(
  async (
    context,
    environment,
    props: {
      groupId: string;
      icon: string | null;
      name: string;
      description: string | null;
      /** IDs of the tags which will act as folders for this tag */
      folderIds: string[];
      isPrivate: boolean;
    },
  ) => {
    const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

    return {
      forward: async () => {
        const action = await actions.tag.createGroup.action(
          {
            ...props,
            ownerOrganizationId,
          },
          context,
        );

        await write(environment, { action });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * updateGroup
 * -------------------------------------------------------------------------------------------------
 */

export const updateGroup = withActionContext(
  async (
    context,
    environment,
    props: {
      tagId: string;
      icon: string | null;
      name: string;
      description: string | null;
      /** IDs of the tags which will act as folders for this tag */
      folderIds: string[];
    },
  ) => {
    const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

    return {
      forward: async () => {
        const action = await actions.tag.updateGroup.action(
          {
            ...props,
            ownerOrganizationId,
          },
          context,
        );

        await write(environment, { action });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * createLabel
 * -------------------------------------------------------------------------------------------------
 */

export const createLabel = withActionContext(
  async (
    context,
    environment,
    props: {
      labelId: string;
      icon: string | null;
      name: string;
      description: string | null;
    },
  ) => {
    const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

    return {
      forward: async () => {
        const action = await actions.tag.createLabel.action(
          {
            ...props,
            ownerOrganizationId,
          },
          context,
        );

        await write(environment, { action });
      },
      backward: async () => {
        const action = await actions.tag.deleteLabel.action({ labelId: props.labelId }, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", { subject: "Undoing label creation." });
          },
        });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * updateLabel
 * -------------------------------------------------------------------------------------------------
 */

export const updateLabel = withActionContext(
  async (
    context,
    environment,
    props: {
      labelId: string;
      icon: string | null;
      name: string;
      description: string | null;
    },
  ) => {
    return {
      forward: async () => {
        const action = await actions.tag.updateLabel.action(props, context);

        await write(environment, { action });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * subscribeUsersToTag
 * -------------------------------------------------------------------------------------------------
 */

export const subscribeUsersToTag = withActionContext(
  async (
    context,
    environment,
    props: {
      tagId: string;
      userIds: string[];
      subscriptionPreference: TagSubscriptionPreference;
      notifyUsers: boolean;
    },
  ) => {
    const ownerOrganizationId = environment.auth.getAndAssertCurrentUserOwnerOrganizationId();

    return {
      forward: async () => {
        const action = await actions.tag.subscribeUsersToTag.action(
          {
            ...props,
            ownerOrganizationId,
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: `Subscribing users...`,
            });
          },
        });

        if (props.notifyUsers) {
          toast("vanilla", {
            subject: `Subscribing users...Done!`,
            description: `and notifications sent.`,
          });
        } else {
          toast("vanilla", {
            subject: `Subscribing users...Done!`,
          });
        }
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * archiveTag
 * -------------------------------------------------------------------------------------------------
 */

export const archiveTag = withActionContext(
  async (
    context,
    environment,
    props: {
      tagId: string;
      tagType: string;
    },
  ) => {
    return {
      forward: () => innerArchiveTag(context, environment, props),
      backward: () => innerUnarchiveTag(context, environment, props),
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 * unarchiveTag
 * -------------------------------------------------------------------------------------------------
 */

export const unarchiveTag = withActionContext(
  async (
    context,
    environment,
    props: {
      tagId: string;
      tagType: string;
    },
  ) => {
    return {
      forward: () => innerUnarchiveTag(context, environment, props),
      backward: () => innerArchiveTag(context, environment, props),
    };
  },
);

/* -----------------------------------------------------------------------------------------------*/

async function innerArchiveTag(
  context: actions.ActionClientContext,
  environment: ClientEnvironment,
  props: {
    tagId: string;
    tagType: string;
  },
) {
  if (props.tagType === SpecialTagTypeEnum.LABEL) {
    const [tag] = await context.environment.loader.getRecord("tag", props.tagId);

    if (!tag) {
      throw new InvalidActionStateError("[archiveTag] Tag not found");
    }

    const canArchive = await canArchiveLabel(environment, tag as LabelTagRecord);

    if (!canArchive) {
      AlertDialogState.open({
        content: oneLine`
        This label cannot be archived because it is used by one or more inbox section rules.
        Edit your inbox and remove this label from any rules in order to archive it.
      `,
      });

      throw new InvalidActionStateError("[archiveTag] Tag used by one or more inbox subsections", {
        userMessage: "SUPPRESS",
      });
    }
  }

  const action = await actions.tag.archiveTag.action(props, context);

  const type =
    props.tagType === SpecialTagTypeEnum.GROUP ? "group"
    : props.tagType === SpecialTagTypeEnum.LABEL ? "label"
    : "tag";

  await write(environment, {
    action,
    onOptimisticWrite: () => {
      toast("vanilla", {
        subject: `Archived ${type}.`,
      });
    },
  });
}

/* -----------------------------------------------------------------------------------------------*/

async function innerUnarchiveTag(
  context: actions.ActionClientContext,
  environment: ClientEnvironment,
  props: { tagId: string; tagType: string },
) {
  const action = await actions.tag.unarchiveTag.action(props, context);

  const type =
    props.tagType === SpecialTagTypeEnum.GROUP ? "group"
    : props.tagType === SpecialTagTypeEnum.LABEL ? "label"
    : "tag";

  await write(environment, {
    action,
    onOptimisticWrite: () => {
      toast("vanilla", { subject: `Restored ${type}.` });
    },
  });
}

/* -----------------------------------------------------------------------------------------------*/
